'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Promise-based replacement for window.confirm / window.prompt.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete exam?', message: '...', tone: 'danger' }))) return;
 *
 *   const pw = await confirm({ title: 'New password', input: { type: 'password', minLength: 6 } });
 *   if (pw === null) return;           // with `input`, resolves to the string or null
 *
 * Native dialogs block the thread, cannot be styled, are suppressed by some embedded
 * browsers, and `prompt` shows typed passwords in clear text. This dialog traps focus,
 * closes on Escape, and returns focus to whatever opened it.
 */

const ConfirmContext = createContext(null);

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);
  const opener = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      // A second dialog while one is open cancels the first rather than stacking.
      if (resolver.current) resolver.current(options.input ? null : false);
      opener.current = typeof document !== 'undefined' ? document.activeElement : null;
      resolver.current = resolve;
      setDialog({ ...options });
    });
  }, []);

  const close = useCallback((result) => {
    const resolve = resolver.current;
    resolver.current = null;
    setDialog(null);
    if (resolve) resolve(result);
    // Hand focus back to the control that opened the dialog.
    const el = opener.current;
    if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 0);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && <DialogView dialog={dialog} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function DialogView({ dialog, onClose }) {
  const {
    title = 'Are you sure?',
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    tone = 'default',
    input,
  } = dialog;

  const [value, setValue] = useState(input?.defaultValue || '');
  const [error, setError] = useState('');
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const cancelRef = useRef(null);
  const isDanger = tone === 'danger';

  const cancel = useCallback(() => onClose(input ? null : false), [onClose, input]);

  const submit = (e) => {
    e?.preventDefault();
    if (input) {
      const trimmed = input.trim === false ? value : value.trim();
      if (input.required !== false && !trimmed) {
        setError(`${input.label || 'A value'} is required.`);
        return;
      }
      if (input.minLength && trimmed.length < input.minLength) {
        setError(`Must be at least ${input.minLength} characters.`);
        return;
      }
      onClose(trimmed);
    } else {
      onClose(true);
    }
  };

  // Initial focus: the input if there is one; otherwise Cancel for destructive
  // actions (so a stray Enter cannot delete something), Confirm for the rest.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    else if (isDanger && cancelRef.current) cancelRef.current.focus();
    else panelRef.current?.querySelector('[data-confirm]')?.focus();
  }, [isDanger]);

  // Escape cancels; Tab is kept inside the dialog; page behind does not scroll.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [cancel]);

  const titleId = 'ui-dialog-title';
  const descId = 'ui-dialog-desc';

  return (
    <div
      className="ui-dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <form
        ref={panelRef}
        className={`ui-dialog ${isDanger ? 'danger' : ''}`}
        role={isDanger ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? descId : undefined}
        onSubmit={submit}
      >
        <h2 id={titleId}>{title}</h2>
        {message && <p id={descId}>{message}</p>}

        {input && (
          <div className="ui-dialog-field">
            {input.label && <label htmlFor="ui-dialog-input">{input.label}</label>}
            {input.multiline ? (
              <textarea
                id="ui-dialog-input"
                ref={inputRef}
                rows={3}
                value={value}
                placeholder={input.placeholder}
                maxLength={input.maxLength}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError('');
                }}
              />
            ) : (
              <input
                id="ui-dialog-input"
                ref={inputRef}
                type={input.type || 'text'}
                value={value}
                placeholder={input.placeholder}
                maxLength={input.maxLength}
                autoComplete={input.type === 'password' ? 'new-password' : 'off'}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError('');
                }}
              />
            )}
            {error && (
              <span className="ui-dialog-error" role="alert">
                {error}
              </span>
            )}
          </div>
        )}

        <div className="ui-dialog-actions">
          <button type="button" ref={cancelRef} className="ui-btn-secondary" onClick={cancel}>
            {cancelText}
          </button>
          <button type="submit" data-confirm className={isDanger ? 'ui-btn-danger' : 'ui-btn-primary'}>
            {confirmText}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Returns the confirm function. Outside a provider (isolated component tests, for
 * instance) it degrades to the native dialogs instead of crashing.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (ctx) return ctx;

  return async (options = {}) => {
    if (typeof window === 'undefined') return options.input ? null : false;
    const text = [options.title, options.message].filter(Boolean).join('\n\n');
    if (options.input) return window.prompt(text, options.input.defaultValue || '');
    return window.confirm(text);
  };
}
